import { useState } from "react";

import { ParlorHeader } from "@/containers/ParlorDetailContainer/components/ParlorHeader";
import { DirectionsMap } from "@/containers/DirectionsDetailContainer/components/DirectionsMap";
import { BuildingIcon, UrnIcon } from "@/components/FacilityIcons";

import { MubinsoHero, MubinsoHeroSimple } from "./components/MubinsoHero";
import { MubinsoAbout } from "./components/MubinsoAbout";
import { MubinsoCost } from "./components/MubinsoCost";
import { MubinsoFees } from "./components/MubinsoFees";
import { MubinsoProcess } from "./components/MubinsoProcess";
import { MubinsoTrust } from "./components/MubinsoTrust";
import { MubinsoFAQ } from "./components/MubinsoFAQ";
import { MubinsoFixedBar } from "./components/MubinsoFixedBar";
import { customEvent } from "goi_common";
import { useParlorInquiryModal } from "@/hooks/useParlorInquiryModal";
import { UrgentCallBannerSection } from "@/components/UrgentCallBannerExperiment";
import {
  MubinsoBreadcrumb,
  MubinsoGuideCards,
  MubinsoPhoneCta,
  MubinsoPlaceLinks,
  MubinsoPublisher,
} from "./components/MubinsoLinks";
import {
  MubinsoFacilityList,
  MubinsoNearbyParlors,
} from "./components/MubinsoNearby";

import type {
  MubinsoTemplate,
  MubinsoBinsoInfo,
  MubinsoBinsoRow,
  MubinsoFeeRow,
  MubinsoPlaceInfo,
  MubinsoCompareRow,
  MubinsoCostBreakdown,
  MubinsoFacilityLink,
  MubinsoFaq,
  MubinsoNearbyParlor,
  MubinsoStep,
} from "@/utils/buildMubinsoPageProps";

interface MubinsoDetailContainerProps {
  parlorId: number;
  /** constants/mubinsoTemplateB.ts 기준. B 는 레이아웃, 섹션 순서, 제목 문구가 다르다 */
  template: MubinsoTemplate;
  companyName: string;
  address: string;
  sidoName: string;
  sigunguName: string;
  latitude?: number;
  longitude?: number;
  hasCoordinates: boolean;
  mainImage: string | null;
  cost: MubinsoCostBreakdown;
  binsoInfo: MubinsoBinsoInfo | null;
  binsoRows: MubinsoBinsoRow[];
  feeRows: MubinsoFeeRow[];
  placeInfo: MubinsoPlaceInfo;
  compareRows: MubinsoCompareRow[];
  steps: MubinsoStep[];
  faqs: MubinsoFaq[];
  nearbyParlors: MubinsoNearbyParlor[];
  charnels: MubinsoFacilityLink[];
  crematoria: MubinsoFacilityLink[];
  regionHref: string | null;
  serviceHref: string;
}

/**
 * 무빈소 CTA 목적지. 330 실험 적용으로 절차 개편안이 확정된 무빈소 퍼널이다.
 * utm 은 parlor 표면 공통 규칙(utm_source=parlor_goi&utm_campaign={표면}).
 */
const mubinsoFunnelHref = (slot: "cost" | "process" | "fixed") =>
  `${process.env.NEXT_PUBLIC_PLATFORM_WEB_URL}/match/request/?template=mubinso&utm_source=parlor_goi&utm_campaign=mubinso&utm_content=${slot}`;

export const MubinsoDetailContainer = ({
  parlorId,
  template,
  companyName,
  address,
  sidoName,
  sigunguName,
  latitude,
  longitude,
  hasCoordinates,
  mainImage,
  cost,
  binsoInfo,
  binsoRows,
  feeRows,
  placeInfo,
  compareRows,
  steps,
  faqs,
  nearbyParlors,
  charnels,
  crematoria,
  regionHref,
  serviceHref,
}: MubinsoDetailContainerProps) => {
  const { openParlorInquiryModal } = useParlorInquiryModal("prlr_mubinso");
  const isB = template === "B";
  // 헤더 전화 띠지는 첫 화면 임종 배너가 스크롤로 사라진 뒤에만 띄운다(장례식장 상세와 같다)
  const [isBannerScrolledPast, setIsBannerScrolledPast] = useState(false);

  const breadcrumb = (
    <MubinsoBreadcrumb
      parlorId={parlorId}
      companyName={companyName}
      sidoName={sidoName}
      sigunguName={sigunguName}
      regionHref={regionHref}
    />
  );
  const costSection = (
    <MubinsoCost
      parlorId={parlorId}
      companyName={companyName}
      cost={cost}
      ctaHref={mubinsoFunnelHref("cost")}
      serviceHref={serviceHref}
      variant={template}
    />
  );
  const feesSection = (
    <MubinsoFees
      companyName={companyName}
      feeRows={feeRows}
      binsoRows={binsoRows}
      title={isB ? `${companyName} 시설 이용료와 빈소 사용료` : undefined}
    />
  );
  const aboutSection = (
    <MubinsoAbout
      companyName={companyName}
      binsoInfo={binsoInfo}
      compareRows={compareRows}
      variant={template}
    />
  );
  const processSection = (
    <MubinsoProcess
      parlorId={parlorId}
      steps={steps}
      ctaHref={mubinsoFunnelHref("process")}
      title={isB ? `${companyName} 무빈소 2일장 순서` : undefined}
      variant={template}
    />
  );
  const trustSection = <MubinsoTrust parlorId={parlorId} variant={template} />;
  const faqSection = (
    <MubinsoFAQ
      faqs={faqs}
      title={isB ? `${companyName} 무빈소 문의` : undefined}
    />
  );
  const guideSection = <MubinsoGuideCards parlorId={parlorId} />;
  const mapSection = hasCoordinates && latitude && longitude && (
    <div className="mb-map">
      <DirectionsMap
        latitude={latitude}
        longitude={longitude}
        companyName={companyName}
        address={address}
        eventPath="prlr_mubinso"
      />
    </div>
  );
  const placeSection = (
    <MubinsoPlaceLinks
      parlorId={parlorId}
      companyName={companyName}
      address={address}
      placeInfo={placeInfo}
      variant={template}
    />
  );
  const nearbySection = (
    <>
      <MubinsoNearbyParlors parlors={nearbyParlors} companyName={companyName} />

      <div className="mb-divider" />

      <MubinsoFacilityList
        id="mb-charnel-title"
        title={`${companyName} 인근 봉안당(납골당)`}
        icon={<UrnIcon size={16} />}
        facilities={charnels}
        trigger="btn_nearby_charnel"
      />

      <div className="mb-divider" />

      <MubinsoFacilityList
        id="mb-cremat-title"
        title={`${companyName} 인근 화장장`}
        icon={<BuildingIcon size={16} />}
        facilities={crematoria}
        trigger="btn_nearby_cremat"
      />
    </>
  );

  return (
    <div className="mb-page">
      <ParlorHeader
        hidePhoneStrip={!isBannerScrolledPast}
        floatPhoneStrip
        phoneStripLabel="임종 접수 · 24시간"
        onPhoneStripClick={() => {
          customEvent({
            newGtm: { path: "prlr_mubinso", trigger_and_target: "hdr_consult" },
          });
          void openParlorInquiryModal();
        }}
      />
      {/* now 콜 확대 임종 배너. 배너가 사라지면 하단 버튼 대신 위 헤더 띠지가 나온다(하단은 고정 바 자리) */}
      <UrgentCallBannerSection
        page="prlr_mubinso"
        parlor={{ id: parlorId, name: companyName }}
        hideFloating
        onScrolledPastChange={setIsBannerScrolledPast}
        isLive
      />

      {/*
       * B 는 독사운드 강화 템플릿처럼 Hero 를 이름, 주소, 사진만 두고 요금표를 맨 앞에 둔다.
       * 문구만 바꾸면 페이지 구조가 같아 템플릿으로 묶이므로 섹션 순서도 다르게 한다.
       */}
      {isB ? (
        <>
          <MubinsoHeroSimple
            companyName={companyName}
            address={address}
            mainImage={mainImage}
          />
          {breadcrumb}
          {feesSection}
          {costSection}
          {aboutSection}
          {faqSection}
          {processSection}
          {mapSection}
          <div className="mb-divider" />
          {placeSection}
          <div className="mb-divider" />
          {nearbySection}
          {trustSection}
          {guideSection}
        </>
      ) : (
        <>
          <MubinsoHero companyName={companyName} mainImage={mainImage} />
          {breadcrumb}
          {costSection}
          {feesSection}
          {aboutSection}
          {processSection}
          {trustSection}
          {faqSection}
          {guideSection}
          {mapSection}
          <div className="mb-divider" />
          {placeSection}
          <div className="mb-divider" />
          {nearbySection}
        </>
      )}

      <section className="mb-bottom-cta">
        <h2 className="mb-bottom-cta__title">
          {isB
            ? "통화로 먼저 확인해 보세요"
            : "결정이 어려우시면 먼저 통화부터"}
        </h2>
        <p className="mb-bottom-cta__desc">
          {isB
            ? `${companyName} 무빈소 가능 여부와 예상 비용을 무료로 알려드립니다. 상담 후 진행하지 않으셔도 괜찮습니다.`
            : `${companyName}에서 무빈소 장례가 가능한지, 비용이 얼마나 드는지 먼저 확인해 보세요. 상담은 무료이고, 상담을 받으셨다고 해서 진행 의무가 생기지는 않습니다.`}
        </p>
        <MubinsoPhoneCta parlorId={parlorId} slot="bottom" />
        {/* B 는 바로 아래 발행자 블록이 같은 출처를 밝혀 반복하지 않는다 */}
        {!isB && (
          <p className="mb-bottom-cta__source">
            고이장례연구소는 보건복지부 e하늘 장사정보시스템의 공공 데이터를
            기반으로 장례 상담을 진행합니다.
          </p>
        )}
      </section>

      <MubinsoPublisher parlorId={parlorId} variant={template} />

      <p className="mb-disclaimer">
        {isB
          ? "장례식장 비용은 안치실 등 필수 시설 이용료를 2023~2026년 기준으로 추산한 최저 금액입니다. 관, 수의, 운구차량, 인력은 고이 무빈소장에 포함되며 구성에 따라 달라집니다."
          : "안내된 장례식장 예상 비용은 고이장례연구소에서 2023~2026년 기준으로 안치실 등 필수 시설 이용료를 추산한 최저 한도 기준 가격입니다. 관, 수의, 운구차량, 전문 인력 등은 고이 무빈소장 상조 서비스에 포함되며 선택하는 구성에 따라 변동될 수 있습니다."}
      </p>

      <MubinsoFixedBar
        parlorId={parlorId}
        ctaHref={mubinsoFunnelHref("fixed")}
        variant={template}
      />
    </div>
  );
};
